import { isPlatformServer } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    Directive,
    ElementRef,
    inject,
    input,
    PLATFORM_ID,
    ViewEncapsulation,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import type { KeenSliderOptions } from 'keen-slider';
import { from, map, of, switchMap, tap } from 'rxjs';

// oxlint-disable-next-line consistent-type-imports
// Reason: Module-level mutable cache variable requires a value-level type reference, not just a type import
let keenImport: typeof import('keen-slider/keen-slider') | undefined = undefined;

@Component({
    selector: 'div[cdkSlider]',
    host: {
        class: 'keen-slider keen-slider--vertical',
        '[class.one-slide]': 'oneSlide()',
    },
    template: `
        <ng-content />
    `,
    styleUrls: ['./slider.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SliderComponent {
    private destroyRef = inject(DestroyRef);
    private isPlatformServer = isPlatformServer(inject(PLATFORM_ID));
    sliderRef: ElementRef<HTMLDivElement> = inject(ElementRef);

    sliderOptions = input<KeenSliderOptions>();

    private sliderOpt$ = toObservable(this.sliderOptions);

    slider = toSignal(this.createSlider(this.sliderRef));

    oneSlide = computed(() => {
        const opt = this.sliderOptions();
        if (!opt) {
            return true;
        }
        if (opt.slides && typeof opt.slides === 'object' && 'perView' in opt.slides) {
            return typeof opt.slides.perView === 'number' ? opt.slides.perView === 1 : false;
        }

        return true;
    });

    async loadKeenSlider() {
        keenImport ??= await import('keen-slider');
        return keenImport;
    }
    createSlider(sliderRef: ElementRef<HTMLElement>) {
        if (this.isPlatformServer) {
            return of(undefined);
        }
        return from(this.loadKeenSlider()).pipe(
            switchMap((keenImport) => {
                return this.sliderOpt$.pipe(
                    tap(() => this.slider()?.destroy()),

                    map((opt) => {
                        const slider = sliderRef.nativeElement;
                        const instance = new keenImport.default(slider, opt);

                        // Warten bis layout berechnet ist und dann update() ausführen, sonst bleibt der slider leer
                        const observer = new ResizeObserver(() => {
                            if (slider.clientWidth > 0) {
                                instance.update();
                                observer.disconnect();
                            }
                        });

                        observer.observe(slider);
                        // Clean up if the component is destroyed before the observer fires
                        this.destroyRef.onDestroy(() => {
                            observer.disconnect();
                        });

                        return instance;
                    }),
                );
            }),
        );
    }

    goToIndex(i: number): void {
        this.slider()?.moveToIdx(i);
    }

    constructor() {
        this.destroyRef.onDestroy(() => {
            this.slider()?.destroy();
        });
    }
}

@Directive({
    selector: '[cdkSlide]',
    host: {
        class: 'keen-slider__slide',
    },
})
export class SlideDirective {}

export type SliderOptions = KeenSliderOptions;
